import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { IServiceChargeInvoicingSummariesService } from "services/ServiceChargeInvoicingSummariesService";
import {
  ReportURLResponseModel,
  ServiceChargeInvoiceDetailsModel,
  ServiceChargeInvoiceSummaryModel,
} from "services/api/cplapi/CPLAPI";
import { InvoiceDistributionResponseModel } from "services/classes/InvoiceDistributionResponseModel";
import { SERVICE_TYPES } from "setup/dependency/ServiceTypes";
import { container } from "setup/dependency/inversify.config";
import { RootState } from "setup/redux/RootReducer";

type ServiceChargeInvoicingSummariesState = {
  ServiceChargeSummaries: ServiceChargeInvoiceSummaryModel[] | undefined;
  LoadingSummaries: boolean;
  ServiceChargeDetails: ServiceChargeInvoiceDetailsModel | undefined;
  LoadingDetails: boolean;
  EmailProgressResponse: InvoiceDistributionResponseModel | undefined;
  PrintProgressResponse: ReportURLResponseModel | undefined;
  IsPrinting: boolean;
  IsEmailing: boolean;
};

const initialState: ServiceChargeInvoicingSummariesState = {
  ServiceChargeSummaries: undefined,
  LoadingSummaries: false,
  ServiceChargeDetails: undefined,
  LoadingDetails: false,
  EmailProgressResponse: undefined,
  PrintProgressResponse: undefined,
  IsPrinting: false,
  IsEmailing: false,
};

export const ServiceChargeInvoicingSummariesSlice = createSlice({
  name: "ServiceChargeInvoicingSummaries",
  initialState,
  reducers: {
    resetDetails: (state) => {
      state.ServiceChargeDetails = undefined;
    },
    setResendEmailProgressResponse: (state, action) => {
      state.EmailProgressResponse = action.payload;
    },
    setPrintProgressResponse: (state, action) => {
      state.PrintProgressResponse = action.payload;
    },
    setIsEmailing: (state, action) => {
      state.IsEmailing = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getSummaries.pending, (state) => {
        state.LoadingSummaries = true;
      })
      .addCase(getSummaries.fulfilled, (state, action) => {
        state.ServiceChargeSummaries = action.payload;
        state.LoadingSummaries = false;
      })
      .addCase(getSummaries.rejected, (state) => {
        state.LoadingSummaries = false;
      })
      .addCase(getDetails.pending, (state) => {
        state.LoadingDetails = true;
      })
      .addCase(getDetails.fulfilled, (state, action) => {
        state.ServiceChargeDetails = action.payload;
        state.LoadingDetails = false;
      })
      .addCase(getDetails.rejected, (state) => {
        state.LoadingDetails = false;
      })
      .addCase(resendServiceChargeInvoicePrints.pending, (state) => {
        state.IsPrinting = true;
      })
      .addCase(resendServiceChargeInvoicePrints.rejected, (state) => {
        state.IsPrinting = false;
      })
      .addCase(resendServiceChargeInvoicePrints.fulfilled, (state, action) => {
        state.PrintProgressResponse = action.payload;
        state.IsPrinting = false;
      });
  },
});

export const getSummaries = createAsyncThunk<ServiceChargeInvoiceSummaryModel[], void>(
  "ServiceChargeInvoicingSummaries/GetSummaries",
  async () => {
    const serviceChargeInvoicingSummariesService = container.get<IServiceChargeInvoicingSummariesService>(
      SERVICE_TYPES.ServiceChargeInvoicingSummariesService
    );
    return await serviceChargeInvoicingSummariesService.getServiceChargeInvoiceSummaries();
  }
);

export const getDetails = createAsyncThunk<ServiceChargeInvoiceSummaryModel, number>(
  "ServiceChargeInvoicingSummaries/GetDetails",
  async (logID) => {
    const serviceChargeInvoicingSummariesService = container.get<IServiceChargeInvoicingSummariesService>(
      SERVICE_TYPES.ServiceChargeInvoicingSummariesService
    );

    return await serviceChargeInvoicingSummariesService.getServiceChargeInvoiceDetails(logID);
  }
);

export const downloadInvoiceFile = createAsyncThunk(
  "ServiceChargeInvoicingSummaries/DownloadInvoiceFile",
  async (breakdownID: number) => {
    const serviceChargeInvoicingSummariesService = container.get<IServiceChargeInvoicingSummariesService>(
      SERVICE_TYPES.ServiceChargeInvoicingSummariesService
    );
    return await serviceChargeInvoicingSummariesService.downloadServiceChargeInvoice(breakdownID);
  }
);

export const resendServiceChargeInvoiceEmails = createAsyncThunk(
  "ServiceChargeInvoicingSummaries/ResendServiceChargeInvoiceEmails",
  async (breakdownIDs: number[]) => {
    const serviceChargeInvoicingSummariesService = container.get<IServiceChargeInvoicingSummariesService>(
      SERVICE_TYPES.ServiceChargeInvoicingSummariesService
    );
    return await serviceChargeInvoicingSummariesService.resendServiceChargeInvoiceEmails(breakdownIDs);
  }
);

export const resendServiceChargeInvoicePrints = createAsyncThunk<ReportURLResponseModel, number[]>(
  "ServiceChargeInvoicingSummaries/ResendServiceChargeInvoicePrints",
  async (breakdownIDs) => {
    const serviceChargeInvoicingSummariesService = container.get<IServiceChargeInvoicingSummariesService>(
      SERVICE_TYPES.ServiceChargeInvoicingSummariesService
    );
    return await serviceChargeInvoicingSummariesService.resendServiceChargeInvoicePrints(breakdownIDs);
  }
);

export const { resetDetails, setResendEmailProgressResponse, setPrintProgressResponse, setIsEmailing } =
  ServiceChargeInvoicingSummariesSlice.actions;

export const selectServiceChargeSummaries = (state: RootState) =>
  state.serviceChargeInvoiceSummaries.ServiceChargeSummaries;
export const selectLoadingSummaries = (state: RootState) => state.serviceChargeInvoiceSummaries.LoadingSummaries;
export const selectServiceChargeDetails = (state: RootState) =>
  state.serviceChargeInvoiceSummaries.ServiceChargeDetails;
export const selectLoadingDetails = (state: RootState) => state.serviceChargeInvoiceSummaries.LoadingDetails;
export const selectPrintProgressResponse = (state: RootState) =>
  state.serviceChargeInvoiceSummaries.PrintProgressResponse;
export const selectEmailProgressResponse = (state: RootState) =>
  state.serviceChargeInvoiceSummaries.EmailProgressResponse;
export const selectIsPrinting = (state: RootState) => state.serviceChargeInvoiceSummaries.IsPrinting;
export const selectIsEmailing = (state: RootState) => state.serviceChargeInvoiceSummaries.IsEmailing;

export default ServiceChargeInvoicingSummariesSlice.reducer;
